import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TablePagination,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import OfflinePinIcon from "@mui/icons-material/OfflinePin";
import configServ from "../services/config";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";

const Approve_LeaveTable = ({ ischange }) => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [selectedLeave, setSelectedLeave] = useState(null);
  const [open, setOpen] = useState(false);

  const [openApproveModal, setOpenApproveModal] = useState(false);
  const [selectedLeaveForApprove, setSelectedLeaveForApprove] = useState(null);
  const [approveStatus, setApproveStatus] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [allLeaveData, setAllLeaveData] = useState([]);
  const [leaveData, setLeaveData] = useState([]);
  const [loading, setLoading] = useState(false);

  const { company_id, userId } = useSelector((state) => state.globalVariables);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  const truncateText = (text, limit = 20) => {
    if (!text) return "-";
    return text.length > limit ? text.substring(0, limit) + "..." : text;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return "warning";
      case "approved":
        return "success";
      case "rejected":
        return "error";
      case "cancelled":
        return "default";
      default:
        return "default";
    }
  };

  const fetchLeaveData = async () => {
    setLoading(true);
    try {
      const customerId = userId || 8;
      const params = { customer_id: customerId };
      const response = await configServ.subordinateCustomerLeaveList(params);
      const data = response?.data ?? response;

      if (data?.success === true) {
        const leaves = Array.isArray(data.leaves) ? data.leaves : [];
        setAllLeaveData(leaves);
        setTotal(leaves.length);
        setPage(0);
      } else {
        setAllLeaveData([]);
        setTotal(0);
      }
    } catch (error) {
      console.error("SUBORDINATE LEAVE ERROR:", error);
      setAllLeaveData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaveData();
  }, [ischange]);

  useEffect(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    setLeaveData(allLeaveData.slice(start, end));
  }, [allLeaveData, page, rowsPerPage]);

  const handleOpen = (leave) => {
    setSelectedLeave(leave);
    setOpen(true);
  };

  const handleOpenApproveModal = (leave) => {
    setSelectedLeaveForApprove(leave);
    setApproveStatus("");
    setOpenApproveModal(true);
  };

  const handleApproveSubmit = async () => {
    if (!approveStatus) {
      toast.error("Please select a status");
      return;
    }

    setSubmitting(true);

    try {
      const response = await configServ.updateLeaveStatus(selectedLeaveForApprove.id, {
        status: approveStatus,
      });

      const isSuccess = response?.status === 200 || response?.data?.success === true;

      if (isSuccess) {
        toast.success(`Leave ${approveStatus} successfully`);
        setOpenApproveModal(false);
        setApproveStatus("");
        await fetchLeaveData();
      } else {
        toast.error(response?.data?.message || "Failed to update status");
      }
    } catch (error) {
      console.error("Status update error:", error);
      toast.error(error?.response?.data?.message || "Something went wrong!");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ width: "100%", p: { xs: 1, sm: 2 } }}>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", minHeight: 300, alignItems: "center" }}>
          <CircularProgress />
        </Box>
      ) : leaveData.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: "center" }}>
          <Typography>No Leave Data Found</Typography>
        </Paper>
      ) : (
        <>
          {/* ── Card View (mobile/tablet) ── */}
          {isMobileOrTablet ? (
            <Stack spacing={2} sx={{ width: "100%", py: 1 }}>
              {leaveData.map((row) => (
                <Card key={row.id} sx={{ border: "1px solid #e2e8f0", borderRadius: 2, boxShadow: "none" }}>
                  <CardContent sx={{ p: 2 }}>
                    <Stack spacing={1.5}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#1e293b" }}>
                          {row.employee_name || "-"}
                        </Typography>
                        <Chip
                          label={row.status ? row.status.toUpperCase() : "-"}
                          color={getStatusColor(row.status)}
                          size="small"
                        />
                      </Box>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "space-between" }}>
                        <Typography variant="body2" sx={{ color: "#64748b" }}>
                          <span style={{ fontWeight: 600, color: "#1e293b" }}>Leave Type:</span> {row.leave_name || "-"}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#64748b" }}>
                          <span style={{ fontWeight: 600, color: "#1e293b" }}>Days:</span> {row.total_days || 0}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "space-between" }}>
                        <Typography variant="body2" sx={{ color: "#64748b" }}>
                          <span style={{ fontWeight: 600, color: "#1e293b" }}>From:</span> {row.from_date || "-"}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#64748b" }}>
                          <span style={{ fontWeight: 600, color: "#1e293b" }}>To:</span> {row.to_date || "-"}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: "#64748b" }}>
                        <span style={{ fontWeight: 600, color: "#1e293b" }}>Reason:</span> {row.reason || "-"}
                      </Typography>
                      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1 }}>
                        <IconButton onClick={() => handleOpen(row)} size="small" sx={{ color: "#6c5ce7" }}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton onClick={() => handleOpenApproveModal(row)} size="small" color="secondary">
                          <OfflinePinIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          ) : (
            /* ── Table View (desktop) ── */
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: isMobile ? 600 : 700 }}>
                <TableHead sx={{ backgroundColor: "#1976d2" }}>
                  <TableRow>
                    <TableCell sx={{ color: "#fff" }}>S.No</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Emp Name</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Leave Type</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Reason</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Duration</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Days</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Status</TableCell>
                    <TableCell sx={{ color: "#fff" }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {leaveData.map((row, index) => (
                    <TableRow key={row.id}>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>{row.employee_name || "-"}</TableCell>
                      <TableCell>{row.leave_name || "-"}</TableCell>
                      <TableCell>
                        <Tooltip title={row.reason || "-"}>
                          <span>{truncateText(row.reason, 15)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {row.from_date || "-"} <br /> <small>to</small> <br /> {row.to_date || "-"}
                      </TableCell>
                      <TableCell>{row.total_days || 0}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.status ? row.status.toUpperCase() : "-"}
                          color={getStatusColor(row.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton onClick={() => handleOpen(row)}>
                          <VisibilityIcon />
                        </IconButton>
                        <IconButton onClick={() => handleOpenApproveModal(row)} color="secondary">
                          <OfflinePinIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              "& .MuiTablePagination-toolbar": {
                flexWrap: "wrap",
                justifyContent: isMobile ? "center" : "flex-end",
              },
            }}
          />
        </>
      )}

      {/* ── Details Dialog ── */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 4,
            p: isMobile ? 1 : 0,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, color: "#1e293b" }}>Leave Details</DialogTitle>
        <DialogContent dividers sx={{ borderColor: "#e2e8f0" }}>
          {selectedLeave && (
            <Stack spacing={1.5}>
              <Typography><b>Employee:</b> {selectedLeave.employee_name || "-"}</Typography>
              <Typography><b>Leave Type:</b> {selectedLeave.leave_name || "-"}</Typography>
              <Typography><b>Holiday Type:</b> {selectedLeave.holiday_title || "-"}</Typography>
              <Typography><b>Reason:</b> {selectedLeave.reason || "-"}</Typography>
              <Typography><b>From:</b> {selectedLeave.from_date || "-"}</Typography>
              <Typography><b>To:</b> {selectedLeave.to_date || "-"}</Typography>
              <Typography><b>Days:</b> {selectedLeave.total_days || 0}</Typography>
              <Typography><b>Status:</b> {selectedLeave.status || "-"}</Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpen(false)} sx={{ color: "#64748b" }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Approve/Reject Modal ── */}
      <Dialog
        open={openApproveModal}
        onClose={() => {
          if (!submitting) setOpenApproveModal(false);
        }}
        fullWidth
        maxWidth="xs"
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 4,
            p: isMobile ? 2 : 3,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, color: "#1e293b", pb: 1 }}>
          Update Leave Status
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: "#e2e8f0", pt: 2 }}>
          <Typography variant="body2" sx={{ color: "#64748b", mb: 3 }}>
            Please select an action to proceed:
          </Typography>

          <FormControl fullWidth size="small">
            <InputLabel id="status-select-label">Select Status</InputLabel>
            <Select
              labelId="status-select-label"
              value={approveStatus}
              label="Select Status"
              onChange={(e) => setApproveStatus(e.target.value)}
              sx={{ bgcolor: "#fff" }}
              disabled={submitting}
            >
              <MenuItem value="approved">Approve</MenuItem>
              <MenuItem value="rejected">Reject</MenuItem>
              <MenuItem value="cancelled">Cancel Leave</MenuItem>
            </Select>
          </FormControl>

          {selectedLeaveForApprove && (
            <Box
              sx={{
                mt: 3,
                p: 2,
                bgcolor: "#f8fafc",
                borderRadius: 2,
                border: "1px solid #e2e8f0",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#0f172a" }}>
                {selectedLeaveForApprove.employee_name || "-"}
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b" }}>
                <b>Leave Type:</b> {selectedLeaveForApprove.leave_name || "-"}
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b" }}>
                <b>Days:</b> {selectedLeaveForApprove.total_days || 0}
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b" }}>
                <b>From:</b> {selectedLeaveForApprove.from_date || "-"}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={() => setOpenApproveModal(false)}
            disabled={submitting}
            sx={{ color: "#64748b", textTransform: "none" }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleApproveSubmit}
            disabled={submitting}
            sx={{
              bgcolor: "#1976d2",
              color: "#fff",
              textTransform: "none",
              "&:hover": { bgcolor: "#1565c0" },
            }}
          >
            {submitting ? "Submitting..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Approve_LeaveTable;